import { useRef, useEffect, useState, useMemo } from "react";
import { X, Send, Sparkles } from "lucide-react";
import orbieeAvatar from "../../images/Orbiee_Avatra.png";
import { getUserProfile, isAuthenticated } from "../../utils/auth";
import {
  stopOrbieeVoicePlayback,
  flagInterimSegments,
  agentActivityLabel,
} from "../../services/websocket";

/** How long one phrasing of a step stays up before the next takes its place. */
const LOADER_LINE_MS = 2600;

/**
 * ChartBoxCode Component
 * MUI-based chat box component for interacting with Orbiee chatbot
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether chat box is open
 * @param {Array} props.messages - Array of message objects
 * @param {string} props.inputValue - Current input value
 * @param {Function} props.setInputValue - Function to set input value
 * @param {Function} props.onClose - Function to close chat box
 * @param {Function} props.onSendMessage - Function to send message
 * @param {Function} props.onKeyPress - Function to handle key press
 * @param {boolean} props.isCartCollapsed - Whether cart is collapsed
 * @param {Function} props.setIsCartCollapsed - Function to toggle cart
 * @param {number} props.totalItems - Total items in cart
 * @param {Function} props.onSuggestionClick - Function to handle suggestion click
 * @param {boolean} props.isLoading - Whether loading
 * @param {{phase: string, tool: string|null}|null} [props.activity] - What the agent is
 *   doing in the turn in flight, from the socket frames; picks the loader's text
 * @param {string[]} [props.followUps=[]] - The backend's suggestions for what to say
 *   next, shown as chips under the thread (chat only; see followUps.js)
 * @param {Function} [props.onFollowUpClick] - Sends a chip's text as the next message
 * @param {{status: "taken"|"connecting"|"failed"}|null} [props.sessionTakeover] - Set
 *   while the backend has moved this account's session to another device
 *   (sessionTakeover.js): the thread shows the notice with "Continue here" and
 *   the composer is closed, since no turn can leave until the shopper resumes
 * @param {Function} [props.onResumeSession] - "Continue here": take the session back
 * @param {boolean} [props.listenAvatarEvents=true] - Surface avatar background chat in this box
 */
export default function ChartBoxCode({
  isOpen = false,
  messages = [],
  inputValue = "",
  setInputValue,
  onClose,
  onSendMessage,
  onKeyPress,
  isCartCollapsed = true,
  setIsCartCollapsed,
  totalItems = 0,
  onSuggestionClick,
  isLoading = false,
  activity = null,
  followUps = [],
  onFollowUpClick,
  sessionTakeover = null,
  onResumeSession,
  listenAvatarEvents = true,
}) {
  const messagesEndRef = useRef(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(getUserProfile());
  // Signed in at all (the header's `isUserLoggedIn` also wants a name).
  // The composer hint tells a guest to sign in: with no seeded greeting the
  // thread no longer says "please sign-in to continue" (2026-10-28).
  const [signedIn, setSignedIn] = useState(() => isAuthenticated());

  // Check if user is logged in
  useEffect(() => {
    const checkLoginStatus = () => {
      const profile = getUserProfile();
      setUserProfile(profile);
      setSignedIn(isAuthenticated());
      setIsUserLoggedIn(isAuthenticated() && !!profile?.customer_name);
    };

    checkLoginStatus();

    // Listen for auth state changes
    const handleAuthChange = () => {
      setTimeout(checkLoginStatus, 100);
    };

    window.addEventListener("authStateChange", handleAuthChange);
    window.addEventListener("authStateChanged", handleAuthChange);
    return () => {
      window.removeEventListener("authStateChange", handleAuthChange);
      window.removeEventListener("authStateChanged", handleAuthChange);
    };
  }, []);

  const [avatarMessages, setAvatarMessages] = useState([]);
  const messagesRef = useRef(messages);
  const avatarMessagesRef = useRef([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // The loader says what the backend is doing, not what we guess from the
  // shopper's words. The step comes from the socket and only the socket
  // changes it: thinking, then the tool the turn reached for, which holds
  // until the turn ends (or thinking → wrapping when no tool is named). What
  // moves here is the phrasing of that one step, so the bubble never reads
  // as frozen and never says anything the backend is not doing.
  const stepKey = `${activity?.phase ?? ""}:${activity?.tool ?? ""}`;

  // A step that lingers moves on to its next phrasing. The tick is stored
  // with the step it belongs to, so a new step opens on its own first line
  // in the same render instead of inheriting the old step's count.
  const [lineTick, setLineTick] = useState({ step: stepKey, n: 0 });

  // Counted between turns, while the loader is hidden: the same request does
  // not open on the same words twice, and the first line never flickers. The
  // finished turn's tick is dropped with it, or it would offset the next
  // turn's opening line and could land it back on the one just used.
  const [turnCount, setTurnCount] = useState(0);
  useEffect(() => {
    if (isLoading) return;
    setTurnCount((c) => c + 1);
    setLineTick((prev) => (prev.n === 0 ? prev : { step: "", n: 0 }));
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) return undefined;
    const interval = setInterval(() => {
      setLineTick((prev) => ({
        step: stepKey,
        n: prev.step === stepKey ? prev.n + 1 : 1,
      }));
    }, LOADER_LINE_MS);
    return () => clearInterval(interval);
  }, [isLoading, stepKey]);

  const loadingLabel = agentActivityLabel(
    activity,
    turnCount + (lineTick.step === stepKey ? lineTick.n : 0)
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, avatarMessages, isLoading, loadingLabel, followUps, sessionTakeover]);

  // Stop playback on unmount
  useEffect(() => {
    return () => {
      stopOrbieeVoicePlayback();
    };
  }, []);

  // Listen for background avatar chat events to surface them inside the chat box.
  useEffect(() => {
    if (!listenAvatarEvents) return undefined;

    const dedupeExists = (msg) => {
      const allMessages = [...messagesRef.current, ...avatarMessagesRef.current];
      const msgText =
        msg.content || msg.conversation_text || msg.answer || msg.followup || "";
      return allMessages.some((m) => {
        const text =
          m.content || m.conversation_text || m.answer || m.followup || "";
        return m.isUser === msg.isUser && text === msgText;
      });
    };

    const handleBackgroundChat = (event) => {
      const { userText, botText, botTexts, botPreambles } = event.detail || {};
      const additions = [];

      if (userText) {
        const userMsg = {
          id: `avatar-user-${Date.now()}`,
          content: userText,
          isUser: true,
          timestamp: new Date(),
        };
        if (!dedupeExists(userMsg)) {
          additions.push(userMsg);
        }
      }

      const texts = Array.isArray(botTexts)
        ? botTexts.map((t) => String(t ?? "").trim()).filter(Boolean)
        : botText
          ? [String(botText).trim()].filter(Boolean)
          : [];

      const interim = flagInterimSegments(texts, botPreambles);
      texts.forEach((text, i) => {
        const botMsg = {
          id: `avatar-bot-${Date.now()}-${i}`,
          conversation_text: text,
          answer: text,
          isUser: false,
          isInterim: interim[i],
          timestamp: new Date(),
        };
        if (!dedupeExists(botMsg)) {
          additions.push(botMsg);
        }
      });

      if (additions.length) {
        setAvatarMessages((prev) => {
          const next = [...prev, ...additions];
          avatarMessagesRef.current = next;
          return next;
        });
      }
    };

    window.addEventListener("orbieeBackgroundChat", handleBackgroundChat);
    return () =>
      window.removeEventListener("orbieeBackgroundChat", handleBackgroundChat);
  }, [listenAvatarEvents]);

  const displayedMessages = useMemo(
    () => [...messages, ...avatarMessages],
    [messages, avatarMessages]
  );

  const handleKeyPress = (e) => {
    // Pass key press to parent component to handle
    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  // The line under the name belongs to the conversation, not to the plumbing:
  // who Orbiee is here for, and nothing else. Connection trouble surfaces where
  // it is actionable — the message thread — rather than as permanent chrome.
  const firstName =
    isUserLoggedIn && userProfile?.customer_name
      ? userProfile.customer_name.trim().split(" ")[0]
      : "";

  return (
    <div className={`ob-chatpanel ${isOpen ? "ob-open" : ""}`} aria-label="Orbiee assistant">
      {/* ── Header ── */}
      <header className="ob-chathead">
        <span className="ob-chatmark" aria-hidden="true">
          <img src={orbieeAvatar} alt="" />
        </span>

        <div className="ob-chatid">
          <span className="ob-display ob-chatname">Orbiee</span>
          <span className="ob-chatsub">
            {firstName ? `Here for ${firstName}` : "Your personal stylist"}
          </span>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="ob-iconbtn ob-chatclose"
          aria-label="Close chat"
          title="Close chat"
        >
          <X style={{ width: 17, height: 17 }} />
        </button>
      </header>

      {/* ── Messages ── */}
      <div className="ob-chatscroll ob-scroll">
        {displayedMessages.map((msg, index) => {
          const text = msg.isUser
            ? msg.content
            : msg.conversation_text || msg.answer || "";
          const followup = msg.followup_question?.question || msg.followup;

          // Consecutive messages from the same side read as one block: tighter
          // spacing and a squared inner corner. Without avatars, grouping is
          // what tells you where one speaker's turn ends.
          const prev = displayedMessages[index - 1];
          const grouped = !!prev && !!prev.isUser === !!msg.isUser;

          const bubbleClass = msg.isUser
            ? "ob-bub ob-bub-me"
            : msg.isInterim
              ? "ob-bub ob-bub-pre"
              : "ob-bub ob-bub-ai";

          return (
            <div
              key={msg.id}
              className={[
                "ob-msgrow",
                msg.isUser ? "ob-me" : "",
                grouped ? "ob-grouped" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="ob-msgbody">
                <div
                  className={bubbleClass}
                  // Screen readers get the distinction the italics give the eye.
                  aria-label={msg.isInterim ? `Orbiee is working: ${text}` : undefined}
                >
                  {text}
                </div>

                {/* Follow-up question + suggested answers */}
                {!msg.isUser && followup && (
                  <div className="ob-followupwrap">
                    <div className="ob-followup">{followup}</div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {msg.followup_question?.suggested_answers?.map(
                        (answer, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() =>
                              onSuggestionClick && onSuggestionClick(answer)
                            }
                            className="ob-suggest"
                          >
                            {answer}
                          </button>
                        )
                      ) || (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              onSuggestionClick && onSuggestionClick("Yes")
                            }
                            className="ob-suggest ob-suggest-yes"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              onSuggestionClick && onSuggestionClick("No")
                            }
                            className="ob-suggest"
                          >
                            No
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* "Signed in elsewhere": the session moved to another device and
            the socket here is cut for good unless the shopper takes it
            back. The app speaking, not Orbiee, so a card rather than a
            bubble; the one action is the way back. */}
        {sessionTakeover && (
          <div className="ob-takeover" role="status" aria-live="polite">
            <span className="ob-takeover-eyebrow">Signed in on another device</span>
            <p className="ob-takeover-text">
              Your conversation moved to another device, so Orbiee has
              disconnected here. You're still signed in — continue here to
              pick up on this device instead.
            </p>
            {sessionTakeover.status === "failed" && (
              <p className="ob-takeover-err">
                Couldn't reconnect. Check your connection and try again.
              </p>
            )}
            <button
              type="button"
              className="ob-btn ob-btn-agent ob-btn-sm"
              onClick={() => onResumeSession && onResumeSession()}
              disabled={sessionTakeover.status === "connecting"}
            >
              {sessionTakeover.status === "connecting"
                ? "Connecting…"
                : sessionTakeover.status === "failed"
                  ? "Try again"
                  : "Continue here"}
            </button>
          </div>
        )}

        {isLoading && (
          <div
            className={`ob-msgrow ${
              displayedMessages.length &&
              !displayedMessages[displayedMessages.length - 1].isUser
                ? "ob-grouped"
                : ""
            }`}
            role="status"
            aria-live="polite"
            aria-label="Assistant is typing"
          >
            <div className="ob-msgbody">
              <div className="ob-bub ob-bub-ai ob-bub-typing">
                <span className="inline-flex items-center gap-1" aria-hidden="true">
                  <span className="ob-tdot" />
                  <span className="ob-tdot" />
                  <span className="ob-tdot" />
                </span>
                {/* Keyed so each new line rises in instead of snapping. */}
                <span key={loadingLabel} className="ob-typelabel">
                  {loadingLabel}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Follow-up suggestions — the backend's "you could say next", chat
            only. They arrive after the reply and rise in one by one; hidden
            while the next turn is in flight. */}
        {!isLoading && !sessionTakeover && followUps.length > 0 && (
          <div
            className="ob-followups"
            role="group"
            aria-label="Suggestions for what to ask next"
          >
            <span className="ob-followups-lead" aria-hidden="true">
              <Sparkles style={{ width: 11, height: 11 }} />
              Ask next
            </span>
            {followUps.map((item, i) => (
              <button
                key={item}
                type="button"
                className="ob-followup-chip"
                style={{ "--i": i }}
                onClick={() => onFollowUpClick && onFollowUpClick(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Composer ── */}
      <div className="ob-chatfoot">
        <div className="ob-composer">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue && setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Ask for something…"
            disabled={isLoading || !!sessionTakeover}
            className="ob-composer-input"
            aria-label="Message Orbiee"
          />
          <button
            type="button"
            onClick={() => onSendMessage && onSendMessage()}
            disabled={isLoading || !!sessionTakeover || !inputValue.trim()}
            className="ob-sendbtn"
            aria-label="Send message"
            title="Send message"
          >
            <Send style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div className="ob-chathint">
          {sessionTakeover
            ? "Tap Continue here to keep chatting on this device."
            : signedIn
              ? "Everything you click on the store, Orbiee can see."
              : "Sign in to chat with Orbiee."}
        </div>
      </div>
    </div>
  );
}
